import { IfpLoansAnalyticsContext } from '@sec-loans-ui/ifp-loans/analytics/analyticsContext'
import { FormContextValue } from '@sec-loans-ui/ifp-loans/types'
import { getFallbackExitUrls } from '@sec-loans-ui/utils/helpers'
import { assertValue } from '@shared/helpers'
import { getOpenAbortModalEvent } from '@ui-common/components/Modal'
import { usePersistingActor } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { commonExit, getLanguage } from '@ui-common/utils'
import { shallowEqual, useSelector } from '@xstate/react'
import { use, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { ActorRefFrom, SnapshotFrom } from 'xstate'

import { IfpWizardData } from './types'
import { useFormStepState } from './useFormStepState'
import { createWizardMachine, WizardContext, WizardMachineType, WizardStep } from './wizardMachine'

const selectValues = ({
  context: { user, wizardData, isEditing, authToken, exitUrls, applicationId, loan, currentApplicantInfoIndex },
  value,
}: SnapshotFrom<WizardMachineType>) => ({
  applicationId,
  user,
  wizardData,
  isEditing,
  authToken,
  exitUrls,
  loan,
  step: value,
  currentApplicantInfoIndex,
})

interface UseWizardLogicReturn extends FormContextValue {
  actor: ActorRefFrom<WizardMachineType>
}

export const useWizardLogic = (initialContext: Partial<WizardContext>): UseWizardLogicReturn => {
  const intl = useIntl()
  const { analyticsService } = use(IfpLoansAnalyticsContext)

  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const wizardMachine = createWizardMachine()
  const actor = usePersistingActor<WizardMachineType>(
    wizardMachine,
    undefined,
    undefined,
    ensureInitialContextIsValid(initialContext),
  )
  const send = actor.send

  const goBackToStatus = useCallback(() => {
    send({ type: 'BACK_TO_STATUS' })
  }, [send])

  const nextStep = useCallback(
    (data: Partial<IfpWizardData>) => {
      send({ type: 'NEXT_STEP', data })
    },
    [send],
  )

  const exit = async () => {
    analyticsService.sendCustomEvent(step, 'AbortButton')
    await commonExit((exitUrls ?? getFallbackExitUrls()).successUrl[getLanguage()])
  }

  const { user, wizardData, isEditing, authToken, exitUrls, applicationId, loan, step, currentApplicantInfoIndex } =
    useSelector(actor, selectValues, shallowEqual)

  const {
    isSubmitting: isStepSubmitting,
    back,
    submit: _submitStep,
    hasSubmitFailed,
  } = useFormStepState(applicationId, {
    toSummary: goBackToStatus,
    previous: useCallback(() => {
      send({ type: 'PREVIOUS_STEP' })
    }, [send]),
    proceed: nextStep,
    exit,
  })

  const submitStep = useCallback(
    (data: Partial<IfpWizardData>) => {
      _submitStep({ ...wizardData, ...data })
    },
    [_submitStep, wizardData],
  )

  return {
    actor,
    applicationId,
    authToken,
    step,
    user,
    loan,
    wizardData,
    isEditing,
    isStepSubmitting,
    hasSubmitFailed,
    currentApplicantInfoIndex,
    exit,
    exitWithConfirmDialog: () => {
      modalSend(getOpenAbortModalEvent(() => exit(), intl))
    },
    goBackToStatus,
    goBack: () => back(false),
    proceed: useCallback(
      (data: Partial<IfpWizardData>) => {
        step === WizardStep.StatusStep ? nextStep(data) : submitStep(data)
      },
      [nextStep, step, submitStep],
    ),
  }
}

const ensureInitialContextIsValid = (initialContext: Partial<WizardContext>): WizardContext => ({
  ...initialContext,
  user: assertValue(initialContext.user, 'initialContext.user'),
  applicationId: assertValue(initialContext.applicationId, 'initialContext.applicationId'),
  loan: assertValue(initialContext.loan, 'initialContext.loan'),
  isNewApplication: assertValue(initialContext.isNewApplication, 'initialContext.isNewApplication'),
  currentApplicantInfoIndex: 0,
})
